import { Clock, MapPin, Navigation, Phone, Search } from 'lucide-react';
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import type { StorefrontLocation, StorefrontLocations } from '@/features/storefront/api/locations';
import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import type { StoreLocatorSection } from '@/features/storefront/sections/schema';
import {
  addressLine,
  directionsUrl,
  isOpenNow,
  matchesSearch,
  osmEmbedUrl,
  telHref,
  wallClock,
  weekdayName,
} from '@/features/storefront/sections/storeLocator';
import { STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';

/**
 * The shop's physical branches: where they are, when they are open, and how
 * to get there.
 *
 * The branches are the merchant's rows (`/storefront/locations`), never the
 * section's - so the live shop draws nothing while there are none, and the
 * customiser says where to add them instead. "Open now" is read on the
 * store's clock (`stores.timezone`), not the shopper's: a branch in Pune is
 * open at nine in Pune whatever time it is in the shopper's phone.
 *
 * Directions are a plain Google Maps link and the map an OpenStreetMap embed;
 * neither needs a key, a script or a paid service.
 */

export interface StoreLocatorViewProps {
  section: StoreLocatorSection;
  /** `null` while the list is still on its way. */
  data: StorefrontLocations | null;
  /** The customiser: says why the section is empty instead of vanishing. */
  preview: boolean;
  heading: ReactNode;
  /** What the customiser shows while the shop has no active location. */
  emptyHint: ReactNode;
}

const MUTED: CSSProperties = { color: 'var(--storefront-muted)' };
const LINE: CSSProperties = { borderColor: 'color-mix(in srgb, currentColor 12%, transparent)' };

/** "Now", refreshed each minute so a badge turns over at closing time. */
function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function OpenBadge({ open }: { open: boolean }) {
  const copy = useShopContent();
  return (
    <span
      data-open-now={open ? 'true' : 'false'}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{
        backgroundColor: open
          ? 'color-mix(in srgb, #16a34a 14%, transparent)'
          : 'color-mix(in srgb, currentColor 8%, transparent)',
        color: open ? '#15803d' : 'var(--storefront-muted)',
      }}
    >
      <span
        aria-hidden
        className="size-1.5 rounded-full"
        style={{ backgroundColor: open ? '#16a34a' : 'currentColor' }}
      />
      {open ? copy('locator.open_now') : copy('locator.closed_now')}
    </span>
  );
}

function HoursList({ location, zone, now }: { location: StorefrontLocation; zone: string; now: Date }) {
  const copy = useShopContent();
  const { locale } = useStorefrontLanguage();
  const today = wallClock(now, zone).weekday;
  return (
    <div className="mt-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={MUTED}>
        <Clock className="size-3.5" aria-hidden />
        {copy('locator.hours')}
      </p>
      <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm">
        {[0, 1, 2, 3, 4, 5, 6].map((weekday) => {
          const row = location.opening_hours.find((each) => each.weekday === weekday);
          const shut = !row || row.closed;
          return (
            <div key={weekday} className={cn('contents', weekday === today && 'font-semibold')}>
              <dt>{weekdayName(weekday, locale)}</dt>
              <dd style={shut ? MUTED : undefined}>
                {shut ? copy('locator.closed') : `${row.opens} – ${row.closes}`}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function LocationDetails({
  section,
  location,
  zone,
  now,
}: {
  section: StoreLocatorSection;
  location: StorefrontLocation;
  zone: string;
  now: Date;
}) {
  const copy = useShopContent();
  const open = isOpenNow(location.opening_hours, now, zone);
  const address = addressLine(location);
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 className="text-lg" style={STOREFRONT_HEADING_STYLE}>
          {location.name}
        </h3>
        {section.show_hours && open !== null ? <OpenBadge open={open} /> : null}
      </div>
      <p className="mt-1.5 flex items-start gap-1.5 text-sm" style={MUTED}>
        <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span>{address}</span>
      </p>
      {location.services.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {location.services.map((service) => (
            <li key={service} className="rounded-full border px-2 py-0.5 text-xs" style={LINE}>
              {service}
            </li>
          ))}
        </ul>
      ) : null}
      {section.show_hours && location.opening_hours.length > 0 ? (
        <HoursList location={location} zone={zone} now={now} />
      ) : null}
      {(section.show_phone && location.phone) || section.show_directions ? (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
          {section.show_phone && location.phone ? (
            <a href={telHref(location.phone)} className="inline-flex items-center gap-1.5 hover:underline">
              <Phone className="size-4" aria-hidden />
              {location.phone}
            </a>
          ) : null}
          {section.show_directions ? (
            <a
              href={directionsUrl(location)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:underline"
              style={{ color: 'var(--storefront-primary)' }}
            >
              <Navigation className="size-4" aria-hidden />
              {copy('locator.directions')}
            </a>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function MapFrame({ location }: { location: StorefrontLocation }) {
  const copy = useShopContent();
  const src = osmEmbedUrl(location);
  if (!src) return null;
  return (
    <iframe
      key={location.id}
      title={copy('locator.map_title', { name: location.name })}
      src={src}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      className="h-80 w-full border-0 md:h-full md:min-h-96"
      style={{ borderRadius: 'var(--storefront-radius)' }}
      data-locator-map
    />
  );
}

export function StoreLocatorView({ section, data, preview, heading, emptyHint }: StoreLocatorViewProps) {
  const copy = useShopContent();
  const now = useMinuteClock();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const all = useMemo(() => data?.locations ?? [], [data]);
  const shown = useMemo(
    () => (section.search ? all.filter((location) => matchesSearch(location, query)) : all),
    [all, query, section.search],
  );

  if (all.length === 0) {
    // Still loading, or nothing to show: the live band takes its padding
    // with it, and the customiser explains where the branches come from.
    if (!preview || data === null) return <div data-section-empty hidden />;
    return (
      <Container>
        {heading}
        {emptyHint}
      </Container>
    );
  }

  const zone = data?.timezone ?? 'Asia/Kolkata';
  const withMap = section.layout === 'list_with_map' && all.some((location) => osmEmbedUrl(location));
  const selected =
    shown.find((location) => location.id === selectedId) ??
    shown.find((location) => osmEmbedUrl(location)) ??
    shown[0];

  const searchBox = section.search ? (
    <label className="relative mb-6 block max-w-md">
      <span className="sr-only">{copy('locator.search')}</span>
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2" style={MUTED} aria-hidden />
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={copy('locator.search')}
        className="h-11 w-full border bg-transparent pl-9 pr-3 text-sm focus:outline-none"
        style={{ ...LINE, borderRadius: 'var(--storefront-radius)' }}
      />
    </label>
  ) : null;

  const empty =
    shown.length === 0 ? (
      <p className="py-6 text-sm" style={MUTED} role="status">
        {copy('locator.no_match', { query: query.trim() })}
      </p>
    ) : null;

  let body: ReactNode;
  if (section.layout === 'cards') {
    body = (
      <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((location) => (
          <li
            key={location.id}
            className="border p-5"
            style={{ ...LINE, borderRadius: 'var(--storefront-radius)' }}
            data-location={location.id}
          >
            <LocationDetails section={section} location={location} zone={zone} now={now} />
          </li>
        ))}
      </ul>
    );
  } else {
    const list = (
      <ul className="divide-y" style={LINE}>
        {shown.map((location) => (
          <li
            key={location.id}
            className="py-5 first:pt-0 last:pb-0"
            data-location={location.id}
            data-selected={withMap && selected?.id === location.id ? 'true' : undefined}
          >
            {withMap ? (
              <button
                type="button"
                className="mb-1 text-xs font-semibold uppercase tracking-wide hover:underline"
                style={selected?.id === location.id ? { color: 'var(--storefront-primary)' } : MUTED}
                aria-pressed={selected?.id === location.id}
                onClick={() => setSelectedId(location.id)}
              >
                {copy('locator.show_on_map')}
              </button>
            ) : null}
            <LocationDetails section={section} location={location} zone={zone} now={now} />
          </li>
        ))}
      </ul>
    );
    body = withMap ? (
      <div className="grid gap-8 md:grid-cols-5">
        <div className="md:col-span-2">{list}</div>
        <div className="md:col-span-3">{selected ? <MapFrame location={selected} /> : null}</div>
      </div>
    ) : (
      list
    );
  }

  return (
    <Container>
      {heading}
      {searchBox}
      <div data-locator-layout={section.layout}>{empty ?? body}</div>
    </Container>
  );
}
